import {
  TClipSegment,
  TCollection,
  TPointBoxSegment,
  TPointSegment,
  TPolygonSegment,
  TTimelineItem,
  TTrack,
  TTrackWaypoint,
} from "@perceptron/core";
import {
  Annotation,
  ClipAnnotation,
  CollectionAnnotation,
  TimeBoxAnnotation,
  TimePointAnnotation,
  TimePolygonAnnotation,
  TrackAnnotation,
  TrackWaypoint,
  isClipAnnotation,
  isCollectionAnnotation,
  isTimeBoxAnnotation,
  isTimedPointAnnotation,
  isTimedPolygonAnnotation,
  isTrackAnnotation,
} from "@perceptron/core/types";

/**
 * Annotations as the player's timeline understands them.
 *
 * Shared rather than local to the player's pane because background tracking
 * warms assets that are not on screen: it has to describe a video exactly as
 * the player later will, or the two disagree about what the work is.
 */

// Annotation → timeline-item mapping. Non-timed annotations (image-space boxes, tool calls) are skipped.

function timeBoxToSegment(a: TimeBoxAnnotation): TPointBoxSegment {
  return {
    id: a.id.toString(),
    type: "point_box",
    start: a.timestamp.start,
    end: a.timestamp.end,
    label: a.label,
    coordinates: a.coordinates,
  };
}

function timePointToSegment(a: TimePointAnnotation): TPointSegment {
  return {
    id: a.id.toString(),
    type: "point",
    start: a.timestamp.start,
    end: a.timestamp.end,
    label: a.label,
    coordinates: a.coordinates,
  };
}

function timePolygonToSegment(a: TimePolygonAnnotation): TPolygonSegment {
  return {
    id: a.id.toString(),
    type: "polygon",
    start: a.timestamp.start,
    end: a.timestamp.end,
    label: a.label,
    coordinates: a.coordinates,
  };
}

function clipToSegment(a: ClipAnnotation): TClipSegment {
  return {
    id: a.id.toString(),
    type: "clip",
    start: a.timestamp.start,
    end: a.timestamp.end,
    label: a.label,
  };
}

function waypointToPlayer(w: TrackWaypoint): TTrackWaypoint {
  if (w.kind === "point_box") {
    return { kind: "point_box", time: w.timestamp.start, coordinates: w.coordinates };
  }
  if (w.kind === "point") {
    return { kind: "point", time: w.timestamp.start, coordinates: w.coordinates };
  }
  return { kind: "polygon", time: w.timestamp.start, coordinates: w.coordinates };
}

function trackAnnotationToTrack(a: TrackAnnotation): TTrack {
  return {
    id: a.id.toString(),
    type: "track",
    label: a.label,
    waypointKind: a.waypointKind,
    waypoints: a.waypoints.map(waypointToPlayer),
  };
}

function collectionAnnotationToCollection(a: CollectionAnnotation): TCollection {
  const children: TCollection["children"] = [];
  for (const child of a.children) {
    const mapped = annotationToTimelineItem(child);
    if (mapped !== null) children.push(mapped as TCollection["children"][number]);
  }
  return {
    id: a.id.toString(),
    type: "collection",
    label: a.label,
    children,
  };
}

export function annotationToTimelineItem(a: Annotation): TTimelineItem | null {
  if (isTrackAnnotation(a)) return trackAnnotationToTrack(a);
  if (isCollectionAnnotation(a)) return collectionAnnotationToCollection(a);
  if (isTimeBoxAnnotation(a)) return timeBoxToSegment(a);
  if (isTimedPointAnnotation(a)) return timePointToSegment(a);
  if (isTimedPolygonAnnotation(a)) return timePolygonToSegment(a);
  if (isClipAnnotation(a)) return clipToSegment(a);
  return null;
}

/** Every timed annotation in `annotations`, in order. Untimed ones are skipped. */
export function annotationsToTimelineItems(annotations: readonly Annotation[]): TTimelineItem[] {
  const items: TTimelineItem[] = [];
  for (const annotation of annotations) {
    const mapped = annotationToTimelineItem(annotation);
    if (mapped !== null) items.push(mapped);
  }
  return items;
}
